import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { CheckIcon } from "../../components/ui/icons";
import { cn } from "../../lib/cn";

/**
 * 応募者の面接日程選択（公開・token 照合 / R-12 core 第3ステップ・tst-bak0.1.32）。
 *
 * 応募者は採用（Accepted）まで Cognito アカウントを持たない。ログインを要求すると
 * 「面接に出るためにアカウントが要り、アカウントを作るために面接が要る」堂々巡りになる。
 * そこでメールで渡した token で照合する（顧客のレビュー投稿と同じ考え方）。
 *
 * 従来この画面は存在せず、guide_application.status に 'Interviewing' という値だけがあった。
 * 日程調整は系の外（メールの往復）で行われ、システムは何も知らないままだった。
 */

type Slot = { id: string; startsAt: string; selected: boolean };
type Interview = {
  status: "Proposed" | "Scheduled" | "Completed" | "Canceled";
  durationMinutes: number;
  scheduledAt: string | null;
  meetingUrl: string | null;
  slots: Slot[];
};

// 応募者は世界中に居るので、閲覧端末のタイムゾーンで表示し、その旨を明示する。
function fmt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const localZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// API のエラーコードを、応募者が次に何をすればよいか分かる文言にする。
const MESSAGES: Record<string, string> = {
  interview_not_found: "This scheduling link is not valid. Please use the link from our email.",
  invalid_token: "This scheduling link is not valid. Please use the link from our email.",
  token_expired:
    "This scheduling link has expired. Reply to our interview email and we'll send you new times.",
  interview_canceled:
    "This interview has been canceled. Reply to our email if you think this is a mistake.",
  interview_closed: "This interview has already taken place.",
  already_scheduled:
    "A time has already been confirmed for this interview. Reply to our email if you need to change it.",
  slot_not_found: "That time is no longer available. Please refresh and pick another.",
};

function messageFor(e: unknown): string {
  const code = e instanceof ApiError ? e.message : "";
  return MESSAGES[code] ?? "Something went wrong. Please try again in a moment.";
}

export function InterviewScheduling() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [interview, setInterview] = useState<Interview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setError(MESSAGES.invalid_token!);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api<{ interview: Interview }>(
        `/guide-interviews/${encodeURIComponent(token)}`,
        { token: null },
      );
      setInterview(r.interview);
      setError(null);
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const choose = async (slotId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api(`/guide-interviews/${encodeURIComponent(token)}/select`, {
        method: "POST",
        body: { slotId },
        token: null,
      });
      await load();
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-10">
      <h1 className="text-xl font-bold text-ink">Schedule your guide interview</h1>
      <p className="mt-1 text-sm text-muted">
        Tokyo Signature Tours — thank you for applying. Pick the time that suits you best.
      </p>

      {loading ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-muted">Loading…</p>
        </Card>
      ) : error && !interview ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-ink">{error}</p>
        </Card>
      ) : interview ? (
        <Card className="mt-6 p-6">
          {interview.status === "Scheduled" || interview.status === "Completed" ? (
            <div>
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckIcon className="h-5 w-5" />
                <span className="text-sm font-semibold">Your interview is confirmed</span>
              </div>
              <dl className="mt-4 space-y-3">
                <div>
                  <dt className="text-xs text-muted">When</dt>
                  <dd className="text-sm text-ink">
                    {interview.scheduledAt ? fmt(interview.scheduledAt) : "—"}{" "}
                    <span className="text-muted">({interview.durationMinutes} minutes)</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">Where</dt>
                  <dd className="text-sm text-ink">
                    {interview.meetingUrl ? (
                      <a
                        href={interview.meetingUrl}
                        className="text-accent underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {interview.meetingUrl}
                      </a>
                    ) : (
                      // 会議 URL は管理者が Google Meet で発行して登録する。まだのときは
                      // 「無い」ではなく「これから届く」と伝える（応募者が探しに行かないように）。
                      "We'll email you the meeting link before the interview."
                    )}
                  </dd>
                </div>
              </dl>
              <p className="mt-4 text-xs text-muted">
                Times are shown in your device timezone ({localZone()}). Need to change it? Just
                reply to our interview email.
              </p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-ink">
                Choose one of the times below for your {interview.durationMinutes}-minute online
                interview.
              </p>
              <ul className="mt-4 space-y-2">
                {interview.slots.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => choose(s.id)}
                      className={cn(
                        "w-full rounded-lg border p-4 text-left text-sm transition",
                        "border-line bg-paper hover:bg-canvas",
                        "disabled:cursor-not-allowed disabled:opacity-60",
                      )}
                    >
                      {fmt(s.startsAt)}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-muted">
                Times are shown in your device timezone ({localZone()}). None of these work? Reply
                to our interview email and we'll find another time.
              </p>
              {error && <p className="mt-3 text-sm text-danger">{error}</p>}
              <div className="mt-4">
                <Button variant="outline" size="md" onClick={() => void load()} disabled={busy}>
                  Refresh
                </Button>
              </div>
            </div>
          )}
        </Card>
      ) : null}
    </main>
  );
}
